import {
  IonAlert,
  IonBadge,
  IonButton,
  IonContent,
  IonDatetime,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonModal,
  IonPage,
  IonText,
  IonTextarea,
  useIonToast,
} from "@ionic/react";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Header from "src/components/Header";
import {
  Cita,
  cancelarCita,
  colorEstado,
  etiquetaEstado,
  obtenerCita,
  reagendarCita,
} from "src/services/atencionCliente";

const AtcDetalleCita: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const citaId = parseInt(id, 10);
  const history = useHistory();
  const [present] = useIonToast();

  const [cita, setCita] = useState<Cita | null>(null);
  const [cargando, setCargando] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [modalReagendarAbierto, setModalReagendarAbierto] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState<string>("");
  const [motivoReagendar, setMotivoReagendar] = useState<string>("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const c = await obtenerCita(citaId);
      setCita(c);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al cargar";
      present({ message: msg, duration: 3000, color: "danger" });
    } finally {
      setCargando(false);
    }
  }, [citaId, present]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cancelar = async (motivo: string) => {
    setCargando(true);
    try {
      await cancelarCita(citaId, motivo);
      present({ message: "Cita cancelada", duration: 2500, color: "success" });
      history.replace("/atencion-cliente");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo cancelar";
      present({ message: msg, duration: 3500, color: "danger" });
    } finally {
      setCargando(false);
    }
  };

  const reagendar = async () => {
    if (!nuevaFecha) {
      present({ message: "Elegi una fecha y hora", duration: 2000, color: "warning" });
      return;
    }
    setCargando(true);
    try {
      const nueva = await reagendarCita(citaId, nuevaFecha, motivoReagendar);
      present({ message: "Cita reagendada", duration: 2500, color: "success" });
      setModalReagendarAbierto(false);
      history.replace(`/atencion-cliente/citas/${nueva.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo reagendar";
      present({ message: msg, duration: 3500, color: "danger" });
    } finally {
      setCargando(false);
    }
  };

  if (cargando && !cita) {
    return (
      <IonPage>
        <Header titulo="Detalle de cita" />
        <IonContent>
          <IonLoading isOpen={true} message="Cargando..." />
        </IonContent>
      </IonPage>
    );
  }

  if (!cita) {
    return (
      <IonPage>
        <Header titulo="Detalle de cita" />
        <IonContent>
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <IonText color="danger">Cita no encontrada.</IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const fecha = new Date(cita.fecha_hora_agendada);
  const puedeCancelar = cita.estado === "AGENDADA";
  const puedeReagendar = cita.estado === "AGENDADA";
  const puedeDarFeedback =
    cita.estado === "TERMINADA" && cita.resultado !== "DERIVADA" && !cita.motivo_sensible;

  return (
    <IonPage>
      <Header titulo="Detalle de cita" />
      <IonContent>
        <IonLoading isOpen={cargando} message="Procesando..." />

        <IonList>
          <IonItem lines="none">
            <IonLabel>
              <h2>{cita.motivo}</h2>
              <p>{cita.dpto_destino_nombre}</p>
            </IonLabel>
            <IonBadge slot="end" color={colorEstado(cita.estado)}>
              {etiquetaEstado(cita.estado)}
            </IonBadge>
          </IonItem>

          <IonItem>
            <IonLabel>
              <h3>Fecha y hora</h3>
              <p>
                {fecha.toLocaleDateString("es-PY")} -{" "}
                {fecha.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" })}
              </p>
            </IonLabel>
          </IonItem>

          {cita.observaciones && (
            <IonItem>
              <IonLabel>
                <h3>Observaciones</h3>
                <p style={{ whiteSpace: "pre-wrap" }}>{cita.observaciones}</p>
              </IonLabel>
            </IonItem>
          )}

          {cita.resultado && (
            <IonItem>
              <IonLabel>
                <h3>Resultado</h3>
                <p>{cita.resultado}</p>
              </IonLabel>
            </IonItem>
          )}
        </IonList>

        <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {puedeReagendar && (
            <IonButton expand="block" fill="outline" onClick={() => setModalReagendarAbierto(true)}>
              Reagendar
            </IonButton>
          )}
          {puedeCancelar && (
            <IonButton
              expand="block"
              fill="outline"
              color="danger"
              onClick={() => setConfirmarCancelar(true)}
            >
              Cancelar cita
            </IonButton>
          )}
          {puedeDarFeedback && (
            <IonButton
              expand="block"
              color="success"
              onClick={() => history.push(`/atencion-cliente/citas/${cita.id}/feedback`)}
            >
              Dejar encuesta
            </IonButton>
          )}
        </div>

        <IonAlert
          isOpen={confirmarCancelar}
          onDidDismiss={() => setConfirmarCancelar(false)}
          header="Cancelar cita"
          message="Solo podes cancelar hasta 1h antes. Continuar?"
          inputs={[
            { name: "motivo", type: "textarea", placeholder: "Motivo (opcional)" },
          ]}
          buttons={[
            { text: "Atras", role: "cancel" },
            {
              text: "Cancelar cita",
              handler: (data) => {
                void cancelar(data?.motivo || "");
              },
            },
          ]}
        />

        <IonModal isOpen={modalReagendarAbierto} onDidDismiss={() => setModalReagendarAbierto(false)}>
          <Header titulo="Reagendar" />
          <IonContent>
            <div style={{ padding: "1rem" }}>
              <IonLabel position="stacked">Nueva fecha y hora</IonLabel>
              <IonDatetime
                displayFormat="DD/MM/YYYY HH:mm"
                pickerFormat="DD/MM/YYYY HH:mm"
                cancelText="Cancelar"
                doneText="Listo"
                value={nuevaFecha}
                onIonChange={(e) => setNuevaFecha(String(e.detail.value || ""))}
              />
              <IonLabel position="stacked">Motivo (opcional)</IonLabel>
              <IonTextarea
                value={motivoReagendar}
                onIonChange={(e) => setMotivoReagendar(e.detail.value || "")}
              />
              <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                <IonButton expand="block" fill="outline" onClick={() => setModalReagendarAbierto(false)}>
                  Atras
                </IonButton>
                <IonButton expand="block" onClick={() => void reagendar()}>
                  Reagendar
                </IonButton>
              </div>
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default AtcDetalleCita;
